/**
 * ImageLoader Module
 * Handles progressive image loading with caching and error fallback
 *
 * Loading Strategy:
 * 1. Initial: Load first N frames around starting position
 * 2. Predictive: When dragging, preload frames in drag direction
 * 3. Spiral: Load remaining frames in spiral pattern (alternating directions)
 * 4. Background: Continue loading during idle time
 */

import type { VariantConfig, CachedImage, LoadingState } from '../types';

const PRODUCT_IMAGES_CACHE = 'product-images';

export class ImageLoader {
  private cache: Map<string, CachedImage> = new Map();
  private loadingStates: Map<string, LoadingState> = new Map();
  private abortControllers: Map<string, AbortController> = new Map();
  private predictiveQueue: Map<string, number[]> = new Map();
  private isLoadingPredictive: boolean = false;
  private browserCache: Cache | null = null;

  /**
   * Generate the URL for a specific frame
   */
  private getFrameUrl(variant: VariantConfig, frameIndex: number): string {
    const frameNumber = frameIndex + 1; // 1-indexed file names
    const fileName = variant.filePattern.replace('{n}', String(frameNumber));
    return `${variant.imagePath}/${fileName}`;
  }

  /**
   * Get cache key for a variant frame
   * Uses colorId only since photos are color-based (not combination-based)
   */
  private getCacheKey(colorId: string, frameIndex: number): string {
    return `${colorId}_${frameIndex}`;
  }

  /**
   * Extract colorId from variantId (format: colorId_customId)
   */
  private extractColorId(variantId: string): string {
    return variantId.split('_')[0] || variantId;
  }

  /**
   * Get browser cache instance (lazy init)
   */
  private async getBrowserCache(): Promise<Cache | null> {
    if (this.browserCache) return this.browserCache;

    try {
      if ('caches' in window) {
        this.browserCache = await caches.open(PRODUCT_IMAGES_CACHE);
        return this.browserCache;
      }
    } catch {
      // Cache API not available
    }
    return null;
  }

  /**
   * Load a single image - önce Cache API'den dene, yoksa network'ten
   */
  private async loadImage(url: string): Promise<HTMLImageElement> {
    // 1. Önce Cache API'den dene (offline desteği için)
    try {
      const browserCache = await this.getBrowserCache();
      if (browserCache) {
        const cachedResponse = await browserCache.match(url);
        if (cachedResponse) {
          const blob = await cachedResponse.blob();
          const objectUrl = URL.createObjectURL(blob);

          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              URL.revokeObjectURL(objectUrl);
              resolve(img);
            };
            img.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              reject(new Error(`Failed to load cached: ${url}`));
            };
            img.src = objectUrl;
          });
        }
      }
    } catch {
      // Cache read failed, fallback to network
    }

    // 2. Cache'de yoksa network'ten yükle
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load: ${url}`));
      img.src = url;
    });
  }

  /**
   * Get a frame from cache or load it
   * Not: Hata durumunda cache'lemez, her seferinde yeniden dener
   */
  async getFrame(variant: VariantConfig, frameIndex: number): Promise<HTMLImageElement | null> {
    const cacheKey = this.getCacheKey(variant.colorId, frameIndex);
    const cached = this.cache.get(cacheKey);

    // Sadece başarıyla yüklenmiş frame'leri cache'den döndür
    if (cached?.loaded) {
      return cached.element;
    }

    // Hata cache'i yok - her seferinde yeniden dene
    try {
      const url = this.getFrameUrl(variant, frameIndex);
      const img = await this.loadImage(url);

      this.cache.set(cacheKey, {
        src: url,
        element: img,
        loaded: true,
        error: false,
      });

      return img;
    } catch {
      // Hata durumunda cache'leme - bir sonraki denemede tekrar denenecek
      console.warn(`[ImageLoader] Failed to load: ${this.getFrameUrl(variant, frameIndex)}`);
      return null;
    }
  }

  /**
   * Check if a frame is loaded (by colorId since photos are color-based)
   */
  isFrameLoaded(variantId: string, frameIndex: number): boolean {
    const colorId = this.extractColorId(variantId);
    const cacheKey = this.getCacheKey(colorId, frameIndex);
    const cached = this.cache.get(cacheKey);
    return cached?.loaded ?? false;
  }

  /**
   * Get cached frame synchronously (returns null if not loaded)
   */
  getCachedFrame(variantId: string, frameIndex: number): HTMLImageElement | null {
    const colorId = this.extractColorId(variantId);
    const cacheKey = this.getCacheKey(colorId, frameIndex);
    const cached = this.cache.get(cacheKey);
    return cached?.loaded ? cached.element : null;
  }

  /**
   * Preload initial frames for quick display
   */
  async preloadInitialFrames(
    variant: VariantConfig,
    startFrame: number,
    count: number,
    onProgress?: (loaded: number, total: number) => void
  ): Promise<void> {
    const framesToLoad: number[] = [];

    // Start from current frame and spread outward
    for (let i = 0; i < count && i < variant.frameCount; i++) {
      const frame = (startFrame + i) % variant.frameCount;
      if (!this.isFrameLoaded(variant.colorId, frame)) {
        framesToLoad.push(frame);
      }
    }

    let loaded = 0;
    const total = framesToLoad.length;

    await Promise.all(
      framesToLoad.map(async (frameIndex) => {
        await this.getFrame(variant, frameIndex);
        loaded++;
        onProgress?.(loaded, total);
      })
    );
  }

  /**
   * Load all frames for a variant with progress callback
   */
  async loadAllFrames(
    variant: VariantConfig,
    onProgress?: (state: LoadingState) => void,
    priority?: number[]
  ): Promise<void> {
    // Cancel any existing load for this color
    this.cancelLoad(variant.colorId);

    const controller = new AbortController();
    this.abortControllers.set(variant.colorId, controller);

    const state: LoadingState = {
      total: variant.frameCount,
      loaded: 0,
      failed: 0,
      inProgress: true,
    };
    this.loadingStates.set(variant.colorId, state);

    // Determine load order - priority frames first, then the rest
    const loadOrder: number[] = [];
    const prioritySet = new Set(priority ?? []);

    // Add priority frames first
    if (priority) {
      for (const frame of priority) {
        if (frame >= 0 && frame < variant.frameCount) {
          loadOrder.push(frame);
        }
      }
    }

    // Add remaining frames
    for (let i = 0; i < variant.frameCount; i++) {
      if (!prioritySet.has(i)) {
        loadOrder.push(i);
      }
    }

    // Load frames with concurrency limit
    const concurrency = 4;
    const chunks: number[][] = [];
    for (let i = 0; i < loadOrder.length; i += concurrency) {
      chunks.push(loadOrder.slice(i, i + concurrency));
    }

    for (const chunk of chunks) {
      if (controller.signal.aborted) break;

      await Promise.all(
        chunk.map(async (frameIndex) => {
          if (controller.signal.aborted) return;

          const result = await this.getFrame(variant, frameIndex);
          if (result) {
            state.loaded++;
          } else {
            state.failed++;
          }
          onProgress?.({ ...state });
        })
      );
    }

    state.inProgress = false;
    this.loadingStates.set(variant.colorId, state);
    onProgress?.({ ...state });
  }

  /**
   * Cancel loading for a color (variantId will be converted to colorId)
   */
  cancelLoad(variantId: string): void {
    const colorId = this.extractColorId(variantId);
    const controller = this.abortControllers.get(colorId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(colorId);
    }
  }

  /**
   * Get loading state for a color (variantId will be converted to colorId)
   */
  getLoadingState(variantId: string): LoadingState | null {
    const colorId = this.extractColorId(variantId);
    return this.loadingStates.get(colorId) ?? null;
  }

  /**
   * Clear cache for a specific color (variantId will be converted to colorId)
   */
  clearVariantCache(variantId: string): void {
    const colorId = this.extractColorId(variantId);
    for (const [key] of this.cache) {
      if (key.startsWith(`${colorId}_`)) {
        this.cache.delete(key);
      }
    }
    this.loadingStates.delete(colorId);
  }

  /**
   * Clear all cache
   */
  clearAllCache(): void {
    this.cache.clear();
    this.loadingStates.clear();
    for (const controller of this.abortControllers.values()) {
      controller.abort();
    }
    this.abortControllers.clear();
  }

  /**
   * Get total cache size
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * PREDICTIVE LOADING - Preload frames in drag direction
   * Call this when user starts dragging or changes direction
   */
  predictiveLoad(
    variant: VariantConfig,
    currentFrame: number,
    direction: 1 | -1, // 1 = forward (right), -1 = backward (left)
    count: number = 8
  ): void {
    const queueKey = variant.colorId;
    const queue: number[] = [];

    // Build queue of frames in the drag direction
    for (let i = 1; i <= count; i++) {
      let frame = currentFrame + (i * direction);
      // Wrap around
      frame = ((frame % variant.frameCount) + variant.frameCount) % variant.frameCount;

      if (!this.isFrameLoaded(variant.colorId, frame)) {
        queue.push(frame);
      }
    }

    this.predictiveQueue.set(queueKey, queue);
    this.processPredictiveQueue(variant);
  }

  /**
   * Process predictive queue with high priority
   */
  private async processPredictiveQueue(variant: VariantConfig): Promise<void> {
    if (this.isLoadingPredictive) return;
    this.isLoadingPredictive = true;

    const queueKey = variant.colorId;

    while (true) {
      const queue = this.predictiveQueue.get(queueKey);
      if (!queue || queue.length === 0) break;

      // Take next frame from queue
      const frameIndex = queue.shift()!;
      this.predictiveQueue.set(queueKey, queue);

      // Skip if already loaded
      if (this.isFrameLoaded(variant.colorId, frameIndex)) continue;

      // Load with high priority (no waiting)
      await this.getFrame(variant, frameIndex);
    }

    this.isLoadingPredictive = false;
  }

  /**
   * SPIRAL LOADING - Load frames in spiral pattern from current position
   * This ensures frames closest to current view are loaded first
   */
  async loadInSpiralPattern(
    variant: VariantConfig,
    centerFrame: number,
    onProgress?: (state: LoadingState) => void
  ): Promise<void> {
    this.cancelLoad(variant.colorId);

    const controller = new AbortController();
    this.abortControllers.set(variant.colorId, controller);

    const state: LoadingState = {
      total: variant.frameCount,
      loaded: 0,
      failed: 0,
      inProgress: true,
    };
    this.loadingStates.set(variant.colorId, state);

    // Count already loaded frames
    for (let i = 0; i < variant.frameCount; i++) {
      if (this.isFrameLoaded(variant.colorId, i)) {
        state.loaded++;
      }
    }
    onProgress?.({ ...state });

    // Build spiral order: center, center+1, center-1, center+2, center-2, ...
    const spiralOrder: number[] = [];
    for (let offset = 0; offset < variant.frameCount; offset++) {
      if (offset === 0) {
        spiralOrder.push(centerFrame);
      } else {
        // Forward
        const forward = (centerFrame + offset) % variant.frameCount;
        spiralOrder.push(forward);

        // Backward
        const backward = ((centerFrame - offset) % variant.frameCount + variant.frameCount) % variant.frameCount;
        if (backward !== forward) {
          spiralOrder.push(backward);
        }
      }
    }

    // Remove duplicates and already loaded
    const toLoad = [...new Set(spiralOrder)].filter(
      (frame) => !this.isFrameLoaded(variant.colorId, frame)
    );

    // Load in batches of 3 (less aggressive than full parallel)
    const batchSize = 3;
    for (let i = 0; i < toLoad.length; i += batchSize) {
      if (controller.signal.aborted) break;

      const batch = toLoad.slice(i, i + batchSize);

      await Promise.all(
        batch.map(async (frameIndex) => {
          if (controller.signal.aborted) return;

          const result = await this.getFrame(variant, frameIndex);
          if (result) {
            state.loaded++;
          } else {
            state.failed++;
          }
          onProgress?.({ ...state });
        })
      );

      // Small delay between batches to not block UI
      if (i + batchSize < toLoad.length) {
        await new Promise((r) => setTimeout(r, 50));
      }
    }

    state.inProgress = false;
    this.loadingStates.set(variant.colorId, state);
    onProgress?.({ ...state });
  }

  /**
   * Get nearest loaded frame (for fallback when dragging fast)
   */
  getNearestLoadedFrame(
    variantId: string,
    targetFrame: number,
    frameCount: number
  ): number | null {
    const colorId = this.extractColorId(variantId);
    // Check target first
    if (this.isFrameLoaded(colorId, targetFrame)) {
      return targetFrame;
    }

    // Search in expanding circles
    for (let offset = 1; offset < frameCount; offset++) {
      const forward = (targetFrame + offset) % frameCount;
      const backward = ((targetFrame - offset) % frameCount + frameCount) % frameCount;

      if (this.isFrameLoaded(colorId, forward)) {
        return forward;
      }
      if (this.isFrameLoaded(colorId, backward)) {
        return backward;
      }
    }

    return null;
  }

  /**
   * Load image URL - returns blob URL if cached, otherwise returns original URL
   * Useful for thumbnails and other img elements that need offline support
   */
  async loadImageUrl(url: string): Promise<string> {
    try {
      const browserCache = await this.getBrowserCache();
      if (browserCache) {
        const cachedResponse = await browserCache.match(url);
        if (cachedResponse) {
          const blob = await cachedResponse.blob();
          return URL.createObjectURL(blob);
        }
      }
    } catch {
      // Cache read failed, fallback to original URL
    }
    // Not cached - return original URL
    return url;
  }

  /**
   * Get loading percentage for a color (variantId will be converted to colorId)
   */
  getLoadingPercentage(variantId: string, frameCount: number): number {
    const colorId = this.extractColorId(variantId);
    let loaded = 0;
    for (let i = 0; i < frameCount; i++) {
      if (this.isFrameLoaded(colorId, i)) {
        loaded++;
      }
    }
    return (loaded / frameCount) * 100;
  }
}
